<script lang="ts" setup>
import { UiBtn, UiSelect, UiInput, UiInputCheck, UiIcon } from '@shared/ui';
import { sendBuyForm } from '@entities/kPlus/api';
import { ValidateHelper } from '@shared/lib/helpers';
import { HttpStatusCode } from '@shared/types';
import { specializations, allRegions } from '@data';
import { useUserStore } from '@app/modules/auth/stores/UserStore';
import type { KPlusBuyFormProps } from './types';

const {
  title = `Купить КонсультантПлюс ${new Date().getFullYear()}<br>на максимально<br>привлекательных условиях!`,
  cost = '',
} = defineProps<KPlusBuyFormProps>();

const userStore = useUserStore();
const emit = defineEmits(['close']);

const isSendForm = ref<boolean>(false);

const specialization = ref<string>('');
const company = ref<string>(userStore.currentUser?.profile_data?.f_name || '');
const fullName = ref<string>(userStore.currentUser?.profile_data?.f_name || '');
const region = ref<string>('');
const mobilePhone = ref<string>(userStore.currentUser?.profile_data?.links.phones[0] || '');
const email = ref<string>(userStore.currentUser?.profile_data?.links.emails?.find(email => email.is_main)?.value || '');

const isCheckedPolice = ref<boolean>(true);

const errors = ref({
  specialization: '',
  company: '',
  fullName: '',
  region: '',
  mobilePhone: '',
  email: '',
  isCheckedPolice: '',
});

const clearError = (field: keyof typeof errors.value) => {
  errors.value[field] = '';
};

const validateField = (
  field: keyof typeof errors.value,
  value: string,
  rules: ((value: string) => string | boolean)[],
): boolean => {
  for (const rule of rules) {
    const error = rule(value);
    if (error) {
      errors.value[field] = error as string;
      return false;
    }
  }
  errors.value[field] = '';
  return true;
};

const validateFormData = (): boolean => {
  const isValidSpecialization = validateField('specialization', specialization.value, [
    value => !value?.trim() && 'Поле обязательно для заполнения',
  ]);
  const isValidRegion = validateField('region', region.value, [
    value => !value?.trim() && 'Поле обязательно для заполнения',
  ]);
  const isValidFullName = validateField('fullName', fullName.value, [
    value => !value?.trim() && 'Поле обязательно для заполнения',
    value => value.length < 3 && 'Поле слишком короткое (менее 3 символов)',
  ]);
  const isValidPhone = validateField('mobilePhone', mobilePhone.value, [
    value => !value?.trim() && 'Поле обязательно для заполнения',
    value =>
      !ValidateHelper.phone(value) && 'Неправильно введен номер телефона',
  ]);
  const isValidEmail = validateField('email', email.value, [
    value => !value?.trim() && 'Поле обязательно для заполнения',
    value =>
      !ValidateHelper.email(value)
      && 'Неправильно введен адрес электронной почты',
  ]);
  const isValidPolice = validateField('isCheckedPolice', isCheckedPolice.value ? 'true' : '', [
    value => !value?.trim() && 'Соглашение необходимо принять',
  ]);

  return isValidSpecialization
    && isValidFullName
    && isValidPhone
    && isValidEmail
    && isValidRegion
    && isValidPolice;
};

const sendForm = async () => {
  try {
    const res = await sendBuyForm({
      title: `${title.replaceAll('<br>', ' ')} (${cost})`,
      specialization: specialization.value,
      company: company.value,
      region: region.value,
      fullName: fullName.value,
      phone: mobilePhone.value,
      email: email.value,
    });

    if (res.status && res.status === HttpStatusCode.CREATED) {
      isSendForm.value = true;
    }
  }
  catch (error) {
    console.log(error);
  }
};

const submitForm = () => {
  if (validateFormData()) {
    sendForm();
  }
};
</script>

<template>
  <form
    class="flex flex-col gap-8"
    @submit.prevent="submitForm"
  >
    <div
      v-if="!isSendForm"
      class="flex flex-col gap-8"
    >
      <div class="flex flex-col gap-2">
        <!-- eslint-disable vue/no-v-html -->
        <h2
          class="text-2xl font-bold text-center"
          v-html="title"
        />
        <p class="text-base leading-normal text-center">
          Заполните форму, чтобы узнать цену<br>
          с учетом всех действующих акций<br>
          (актуально для компаний Москвы и МО)
        </p>
      </div>
      <div class="flex flex-col gap-4">
        <UiSelect
          v-model="specialization"
          :options="specializations"
          label-key="title"
          value-key="title"
          empty-label="Выберите специальность"
          :max-height="240"
          :error="errors.specialization"
          @on-select="clearError('specialization')"
        />

        <UiInput
          v-model="company"
          name="company"
          placeholder="Название компании"
          :error="errors.company"
          :required="false"
          @on-focus="clearError('company')"
          @on-enter="submitForm"
        />
        <UiSelect
          v-model="region"
          :options="allRegions"
          label-key="title"
          value-key="title"
          empty-label="Выберите регион"
          :max-height="240"
          :error="errors.region"
          @on-select="clearError('region')"
        />

        <UiInput
          v-model="fullName"
          name="fullName"
          placeholder="Ваше имя"
          :error="errors.fullName"
          :required="true"
          @on-focus="clearError('fullName')"
          @on-enter="submitForm"
        />
        <UiInput
          v-model="mobilePhone"
          name="mobilePhone"
          placeholder="Телефон"
          :error="errors.mobilePhone"
          mask="phone"
          :required="true"
          @on-focus="clearError('mobilePhone')"
          @on-enter="submitForm"
        />
        <UiInput
          v-model="email"
          name="email"
          placeholder="Email"
          :error="errors.email"
          :required="true"
          @on-focus="clearError('email')"
          @on-enter="submitForm"
        />
        <UiBtn
          label="Купить по специальной цене"
          color="purple"
          class="mt-2"
          @click="submitForm"
        />
        <div class="flex flex-col gap-2 self-center">
          <div class="flex items-center justify-center gap-1">
            <UiInputCheck
              v-model="isCheckedPolice"
              color="purple"
              @on-change="clearError('isCheckedPolice')"
            />
            <p class="text-sm">
              Соглашаюсь <a
                href="https://www.4dk.ru/s/privacy"
                target="_blank"
                class="text-purple-1000 underline"
              >с политикой конфиденциальности</a>
            </p>
          </div>
          <span
            v-if="errors.isCheckedPolice"
            class="text-sm text-red-500"
          >{{ errors.isCheckedPolice }}</span>
        </div>
      </div>
    </div>
    <div
      v-else
      class="flex flex-col items-center gap-4"
    >
      <UiIcon
        name="logo_min"
        class="!text-blue-600 !w-[56px] !h-[39px]"
      />
      <h2 class="text-2xl font-bold text-center">
        Спасибо! <br>
        Ваша заявка принята
      </h2>
      <p class="text-base leading-normal text-center">
        Наши специалисты свяжутся с вами<br>в течение рабочего дня
      </p>
      <UiBtn
        label="Закрыть"
        color="purple"
        class="mt-2 w-full"
        @click="emit('close')"
      />
    </div>
  </form>
</template>
